import React, { useState, useMemo } from "react";
import {
  Plus,
  Pencil,
  Trash,
  CurrencyDollar,
  TrendUp,
} from "@phosphor-icons/react";
import { useData } from "../context/DataContext";
import { IncomeSource } from "../types";
import { formatCurrency } from "../utils/calculations";
import DollarInput from "../components/DollarInput";
import { useModal } from "../hooks/useModal";

// Extracted components for better organization
const IncomeSummaryCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  ariaLabel?: string;
}> = ({ title, value, icon, ariaLabel }) => (
  <div className="card" role="region" aria-label={ariaLabel}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600 dark:text-gray-400">{title}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white">
          {value}
        </p>
      </div>
      <div aria-hidden="true">{icon}</div>
    </div>
  </div>
);

const EmptyState: React.FC<{ onAddClick: () => void }> = ({ onAddClick }) => (
  <div className="card" role="region" aria-label="No income sources">
    <div className="text-center py-12">
      <CurrencyDollar
        className="w-12 h-12 text-gray-400 mx-auto mb-4"
        weight="duotone"
        aria-hidden="true"
      />
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        No Income Sources
      </h2>
      <p className="text-gray-600 dark:text-gray-400">
        Add your first income source to start tracking your earnings.
      </p>
      <button
        onClick={onAddClick}
        className="btn-primary"
        aria-label="Add your first income source">
        Add Your First Income Source
      </button>
    </div>
  </div>
);

const IncomeSourceItem: React.FC<{
  incomeSource: IncomeSource;
  onEdit: (incomeSource: IncomeSource) => void;
  onDelete: (id: string) => void;
  getCategoryIcon: (category: string) => React.ReactNode;
  getFrequencyLabel: (frequency: IncomeSource["frequency"]) => string;
}> = ({
  incomeSource,
  onEdit,
  onDelete,
  getCategoryIcon,
  getFrequencyLabel,
}) => (
  <div
    className="flex items-center justify-between p-4 bg-gray-100 dark:bg-gray-700 rounded-lg"
    role="listitem">
    <div className="flex items-center space-x-3">
      <div
        className={`w-3 h-3 rounded-full ${
          incomeSource.isActive
            ? "bg-gradient-to-br from-green-400 to-green-600"
            : "bg-gradient-to-br from-gray-400 to-gray-600"
        }`}
        aria-label={
          incomeSource.isActive
            ? "Active income source"
            : "Inactive income source"
        }
      />
      <div>
        <h4 className="text-gray-900 dark:text-white font-medium">
          {incomeSource.name}
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {getFrequencyLabel(incomeSource.frequency)} •{" "}
          {incomeSource.description || incomeSource.category}
        </p>
      </div>
    </div>
    <div className="flex items-center space-x-4">
      <div className="text-right">
        <p className="text-gray-900 dark:text-white font-medium">
          {formatCurrency(incomeSource.amount)}
        </p>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          {getFrequencyLabel(incomeSource.frequency)}
        </p>
      </div>
      <div
        className="flex items-center space-x-2"
        role="group"
        aria-label={`Actions for ${incomeSource.name}`}>
        <button
          onClick={() => onEdit(incomeSource)}
          className="btn-icon"
          aria-label={`Edit ${incomeSource.name}`}>
          <Pencil className="w-4 h-4 text-gray-400" />
        </button>
        <button
          onClick={() => onDelete(incomeSource.id)}
          className="btn-icon"
          aria-label={`Delete ${incomeSource.name}`}>
          <Trash className="w-4 h-4 text-red-400" />
        </button>
      </div>
    </div>
  </div>
);

const IncomeModal: React.FC<{
  show: boolean;
  editingIncome: IncomeSource | null;
  formData: any;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onInputChange: (field: string, value: any) => void;
  onAmountChange: (value: number) => void;
}> = ({
  show,
  editingIncome,
  formData,
  onClose,
  onSubmit,
  onInputChange,
  onAmountChange,
}) => {
  const modalRef = useModal({ isOpen: show, onClose });

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="income-modal-title">
      <div
        ref={modalRef}
        className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h2
          id="income-modal-title"
          className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          {editingIncome ? "Edit Income Source" : "Add Income Source"}
        </h2>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="income-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Income Name
            </label>
            <input
              id="income-name"
              type="text"
              value={formData.name}
              onChange={(e) => onInputChange("name", e.target.value)}
              className="input w-full"
              placeholder="e.g., Full-time Job, Freelance Project"
              required
              aria-describedby="income-name-help"
            />
            <div id="income-name-help" className="sr-only">
              Enter a descriptive name for this income source
            </div>
          </div>

          <div>
            <label
              htmlFor="income-category"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              id="income-category"
              value={formData.category}
              onChange={(e) => onInputChange("category", e.target.value)}
              className="select w-full">
              <option value="Salary">Salary</option>
              <option value="Freelance">Freelance</option>
              <option value="Investment">Investment</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <DollarInput
            value={formData.amount}
            onChange={onAmountChange}
            placeholder="$0.00"
            label="Amount"
            required
            id="income-amount"
            aria-describedby="income-amount-help"
          />
          <div id="income-amount-help" className="sr-only">
            Enter the amount in dollars
          </div>

          <div>
            <label
              htmlFor="income-frequency"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Frequency
            </label>
            <select
              id="income-frequency"
              value={formData.frequency}
              onChange={(e) => onInputChange("frequency", e.target.value)}
              className="select w-full">
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Bi-weekly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="income-description"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description (Optional)
            </label>
            <textarea
              id="income-description"
              value={formData.description}
              onChange={(e) => onInputChange("description", e.target.value)}
              className="input w-full"
              rows={3}
              placeholder="Additional details about this income source"
              aria-describedby="income-description-help"
            />
            <div id="income-description-help" className="sr-only">
              Optional additional information about this income source
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1">
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1"
              aria-label={
                editingIncome ? "Update income source" : "Add income source"
              }>
              {editingIncome ? "Update Income" : "Add Income"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Income: React.FC = () => {
  const { getDashboardData, dispatch, getCurrentUser } = useData();
  const dashboardData = getDashboardData();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingIncome, setEditingIncome] = useState<IncomeSource | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    amount: 0,
    frequency: "monthly" as IncomeSource["frequency"],
    category: "Salary",
    description: "",
  });

  // Memoize calculations to avoid unnecessary re-renders
  const { incomeSources, incomeByCategory } = useMemo(() => {
    if (!dashboardData) {
      return { incomeSources: [], incomeByCategory: {} };
    }

    const incomeSources = dashboardData?.incomeSources || [];

    // Group income sources by category
    const incomeByCategory = incomeSources.reduce(
      (acc: Record<string, IncomeSource[]>, incomeSource: IncomeSource) => {
        if (!acc[incomeSource.category]) {
          acc[incomeSource.category] = [];
        }
        acc[incomeSource.category].push(incomeSource);
        return acc;
      },
      {} as Record<string, IncomeSource[]>
    );

    return { incomeSources, incomeByCategory };
  }, [dashboardData]);

  if (!dashboardData) {
    return (
      <main className="space-y-6" role="main" aria-label="Income page">
        <div className="card">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Income
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Add your first income source to get started.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary mt-4"
            aria-label="Add income source">
            Add Income Source
          </button>
        </div>
      </main>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const now = new Date();

    // Get the current user ID
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.error("No current user found");
      return;
    }

    // Create income source
    const incomeSource: IncomeSource = {
      id: editingIncome?.id || `income-source-${Date.now()}`,
      userId: currentUser.id,
      name: formData.name,
      category: formData.category,
      amount: formData.amount,
      frequency: formData.frequency,
      description: formData.description,
      isActive: true,
      createdAt: now.toISOString(),
    };

    if (editingIncome) {
      dispatch({ type: "UPDATE_INCOME_SOURCE", payload: incomeSource });
    } else {
      dispatch({ type: "ADD_INCOME_SOURCE", payload: incomeSource });
    }

    setShowAddModal(false);
    setEditingIncome(null);
    setFormData({
      name: "",
      amount: 0,
      frequency: "monthly",
      category: "Salary",
      description: "",
    });
  };

  const handleEdit = (incomeSource: IncomeSource) => {
    setEditingIncome(incomeSource);
    setFormData({
      name: incomeSource.name,
      amount: incomeSource.amount,
      frequency: incomeSource.frequency,
      category: incomeSource.category,
      description: incomeSource.description || "",
    });
    setShowAddModal(true);
  };

  const handleDelete = (incomeSourceId: string) => {
    if (window.confirm("Are you sure you want to delete this income source?")) {
      dispatch({ type: "DELETE_INCOME_SOURCE", payload: incomeSourceId });
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "Salary":
        return (
          <CurrencyDollar className="w-5 h-5 text-green-500" weight="duotone" />
        );
      case "Freelance":
        return <TrendUp className="w-5 h-5 text-blue-500" weight="duotone" />;
      case "Investment":
        return <TrendUp className="w-5 h-5 text-yellow-500" weight="duotone" />;
      default:
        return (
          <CurrencyDollar className="w-5 h-5 text-gray-500" weight="duotone" />
        );
    }
  };

  const getFrequencyLabel = (frequency: IncomeSource["frequency"]) => {
    switch (frequency) {
      case "monthly":
        return "Monthly";
      case "weekly":
        return "Weekly";
      case "biweekly":
        return "Bi-weekly";
      case "yearly":
        return "Yearly";
      default:
        return "Monthly";
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleAmountChange = (value: number) => {
    setFormData({ ...formData, amount: value });
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setEditingIncome(null);
    setFormData({
      name: "",
      amount: 0,
      frequency: "monthly",
      category: "Salary",
      description: "",
    });
  };

  return (
    <main className="space-y-6" role="main" aria-label="Income page">
      <div className="flex items-center justify-between">
        <div></div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center space-x-1 sm:space-x-2"
          aria-label="Add new income source">
          <Plus className="w-4 h-4" aria-hidden="true" />
          <span className="hidden sm:inline">Add Income</span>
          <span className="sm:hidden">Income</span>
        </button>
      </div>

      {/* Summary Cards */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6"
        aria-label="Income summary">
        <IncomeSummaryCard
          title="Monthly Income"
          value={formatCurrency(dashboardData.monthlyIncome)}
          icon={
            <CurrencyDollar
              className="w-8 h-8 text-primary-500"
              weight="duotone"
            />
          }
          ariaLabel="Monthly income summary"
        />
        <IncomeSummaryCard
          title="Income Sources"
          value={incomeSources?.length || 0}
          icon={<TrendUp className="w-8 h-8 text-green-500" weight="duotone" />}
          ariaLabel="Number of income sources"
        />
      </section>

      {/* Income Sources by Category */}
      {Object.keys(incomeByCategory).length === 0 ? (
        <EmptyState onAddClick={() => setShowAddModal(true)} />
      ) : (
        <section className="space-y-6" aria-label="Income sources by category">
          {Object.entries(incomeByCategory).map(([category, sources]) => (
            <div
              key={category}
              className="card"
              role="region"
              aria-label={`${category} income sources`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div aria-hidden="true">{getCategoryIcon(category)}</div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {category}
                  </h2>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-400">Total</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {formatCurrency(
                      (sources as IncomeSource[]).reduce(
                        (sum: number, s: IncomeSource) => sum + s.amount,
                        0
                      )
                    )}
                  </p>
                </div>
              </div>

              <div
                className="space-y-3"
                role="list"
                aria-label={`${category} income sources list`}>
                {(sources as IncomeSource[]).map(
                  (incomeSource: IncomeSource) => (
                    <IncomeSourceItem
                      key={incomeSource.id}
                      incomeSource={incomeSource}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      getCategoryIcon={getCategoryIcon}
                      getFrequencyLabel={getFrequencyLabel}
                    />
                  )
                )}
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Add/Edit Modal */}
      <IncomeModal
        show={showAddModal}
        editingIncome={editingIncome}
        formData={formData}
        onClose={handleModalClose}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
        onAmountChange={handleAmountChange}
      />
    </main>
  );
};

export default Income;
